import { updateStaff, getOrgUnitsByLevel, addUserToUserGroup } from '../../../utils/api'
import { getAuthHeader } from '../../../utils/auth'
import { ROLE_LEVEL_MAP, ROLE_USERGROUP_MAP } from '../api/constants'
import { useDataEngine } from '@dhis2/app-runtime';

const BASE_URL = process.env.REACT_APP_DHIS2_BASE_URL || '';

const STAFF_ROLE_ID = 'R3lFolCMdoi';

export const staffListQuery = (programId) => ({
  staff: {
    resource: 'trackedEntityInstances',
    params: {
      program: programId,
      paging: 1000,
      fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]'
    }
  }
})


// Optional: Helper function to remove user from user group
async function removeUserFromUserGroup(userId, userGroupId, engine) {
  try {
    await engine.mutate({
      resource: `userGroups/${userGroupId}/users/${userId}`,
      type: 'delete'
    });
    return true;
  } catch (error) {
    console.error(`Error removing user ${userId} from group ${userGroupId}:`, error);
    throw error;
  }
}

// Merged update function for user and TEI updates

export async function updateStaffComplete(engine, userId, teiId, staffData, auth) {
  try {
    console.log('🔧 updateStaffComplete called with:', { userId, teiId });
    if (!userId) throw new Error('User ID is required for update');

    // ============ 1. FETCH CURRENT USER (so we NEVER wipe existing fields) ============
    const { user: currentUser } = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'id,username,firstName,surname,email,phoneNumber,organisationUnits[id],userRoles[id],userCredentials[username,userRoles[id]]'
        }
      }
    });
    if (!currentUser) throw new Error(`User ${userId} not found`);

    // ✅ USERNAME IS IMMUTABLE: keep the existing one; only fall back to staffData
    //    if the record was already damaged (username empty)
    const immutableUsername =
      currentUser.username ||
      currentUser.userCredentials?.username ||
      staffData.username;

    // ============ 2. ORG UNITS ============
    const existingOrgUnits = currentUser.organisationUnits || [];
    let finalOrgUnits = [...existingOrgUnits];
    if (staffData.organisationUnits?.length) finalOrgUnits = [...staffData.organisationUnits];
    else if (staffData.orgUnit) finalOrgUnits = [{ id: staffData.orgUnit }];

    // ============ 3. ROLES (fall back to current roles if none provided) ============
    const incomingRoles = staffData.userRoles?.length
      ? staffData.userRoles
      : staffData.userRole ? [staffData.userRole] : [];
    const currentRoleIds = (currentUser.userRoles || currentUser.userCredentials?.userRoles || []).map(r => r.id);
    const roleIds = incomingRoles.length ? incomingRoles : currentRoleIds;
    const userRolesPayload = roleIds.map(id => ({ id }));

    // ============ 4. MERGED USER UPDATE (never destructive) ============
    const userPayload = {
      firstName: staffData.firstName || currentUser.firstName,
      surname: staffData.surname || staffData.secondName || currentUser.surname,
      email: staffData.email || currentUser.email,                       // ← only email changes if that's all you edited
      phoneNumber: staffData.phoneNumber || currentUser.phoneNumber,
      username: immutableUsername,                                       // ✅ ALWAYS sent, never blank
      organisationUnits: finalOrgUnits,
      userRoles: userRolesPayload,
    };
    // Keep userCredentials.username intact (older API versions store it there)
    if (currentUser.userCredentials) {
      userPayload.userCredentials = {
        ...currentUser.userCredentials,
        username: immutableUsername,
        userRoles: userRolesPayload,
      };
    }
    if (staffData.dob) {
      userPayload.attributeValues = [{ attribute: 'Ndsp9fQExRc', value: staffData.dob }];
    }

    console.log('📦 Merged user payload:', userPayload);
    const userUpdateResponse = await engine.mutate({
      resource: 'users',
      id: userId,
      type: 'update',
      data: userPayload
    });
    console.log('✅ User updated — username preserved:', immutableUsername);

    // ============ 5. USER-GROUP SYNC — AFTER the user update (so it isn't wiped) ============
    if (roleIds.length) {
      try {
        const { user: refreshed } = await engine.query({
          user: { resource: `users/${userId}`, params: { fields: 'userGroups[id]' } }
        });
        const currentGroups = refreshed.userGroups || [];
        const targetGroupIds = roleIds.map(r => ROLE_USERGROUP_MAP[r]).filter(Boolean);
        for (const groupId of targetGroupIds) {
          if (!currentGroups.some(g => g.id === groupId)) {
            await addUserToUserGroup(userId, groupId, engine);
          }
        }
      } catch (gErr) {
        console.warn('⚠️ User group sync failed (non-fatal):', gErr.message);
      }
    }

    // ============ 6. TEI UPDATE — MERGE attributes, NEVER delete ============
    let teiUpdateResponse = null;
    let teiExists = false;
    let currentTeiData = null;

    if (teiId) {
      try {
        const { tei } = await engine.query({
          tei: {
            resource: `trackedEntityInstances/${teiId}`,
            params: { fields: 'attributes[attribute,value],orgUnit' }
          }
        });
        currentTeiData = tei;
        teiExists = true;
      } catch (e) {
        teiExists = false;
      }
    }

    if (teiExists && currentTeiData) {
      const existingAttributes = currentTeiData.attributes || [];

      // Values we WANT to set. Empty value ⇒ keep the existing one untouched.
      const desired = {
        n8aAqQTVI8b: userId,
        g3RRIfDiQra: staffData.username || immutableUsername, // username never blank
        HAOvVNYm8Lx: staffData.gender,                        // gender only if provided
        HZG6mhL4FL5: staffData.department || staffData.orgUnitName,
      };

      // Merge in place — attributes we don't touch are preserved exactly as-is
      const mergedAttributes = existingAttributes.map(attr =>
        desired[attr.attribute] ? { ...attr, value: desired[attr.attribute] } : attr
      );
      // Append only the ones that were missing entirely
      Object.entries(desired).forEach(([attribute, value]) => {
        if (value && !mergedAttributes.some(a => a.attribute === attribute)) {
          mergedAttributes.push({ attribute, value });
        }
      });

      const teiPayload = {
        trackedEntityInstance: teiId,
        trackedEntityType: 'OaAbpIs1cV7',
        orgUnit: staffData.orgUnit || currentTeiData.orgUnit,
        attributes: mergedAttributes,
      };

      try {
        await engine.mutate({
          resource: 'trackedEntityInstances',
          id: teiId,
          type: 'update',
          data: teiPayload
        });
        teiUpdateResponse = { success: true };
        console.log('✅ TEI merged safely — no attribute deleted');
      } catch (err) {
        teiUpdateResponse = { success: false, error: err.message };
      }

    } else if (!teiExists && teiId) {
      // teiId was given but not found → do NOT silently create a duplicate
      teiUpdateResponse = { success: false, error: 'TEI not found — skipped creation to avoid duplicates' };

    } else {
      // No teiId at all → create once, guarded
      try {
        const createResult = await engine.mutate({
          resource: 'trackedEntityInstances',
          type: 'create',
          data: {
            trackedEntityType: 'OaAbpIs1cV7',
            orgUnit: staffData.orgUnit || finalOrgUnits[0]?.id,
            attributes: [
              { attribute: 'n8aAqQTVI8b', value: userId },
              { attribute: 'g3RRIfDiQra', value: immutableUsername },
              ...(staffData.gender ? [{ attribute: 'HAOvVNYm8Lx', value: staffData.gender }] : []),
              ...((staffData.department || staffData.orgUnitName)
                ? [{ attribute: 'HZG6mhL4FL5', value: staffData.department || staffData.orgUnitName }] : []),
            ]
          }
        });
        teiUpdateResponse = { success: true, newTei: createResult };
      } catch (err) {
        teiUpdateResponse = { success: false, error: err.message };
      }
    }

    return {
      user: userUpdateResponse,
      tei: teiUpdateResponse,
      teiExists,
      username: immutableUsername,
      note: 'User, roles and department updated safely (username immutable)'
    };
  } catch (error) {
    console.error('❌ updateStaffComplete error:', error);
    throw new Error(`Failed to update staff: ${error.message}`);
  }
}

// Add this debug function to track user group changes
const debugUserGroups = async (engine, userId, operation) => {
  try {
    const response = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'id,username,userGroups[id,name]'
        }
      }
    });

    const userGroups = response.user.userGroups || [];
    console.log(`🔍 DEBUG ${operation}: User ${userId} has ${userGroups.length} user groups:`,
      userGroups.map(g => `${g.name} (${g.id})`).join(', '));

    return userGroups;
  } catch (error) {
    console.error(`🔍 DEBUG ${operation} failed:`, error);
    return [];
  }
};

// ✅ ADD THIS FUNCTION: Get user's current user groups
async function getUserUserGroups(userId, engine) {
  try {
    const response = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: {
          fields: 'userGroups[id,name]'
        }
      }
    });
    return response.user.userGroups || [];
  } catch (error) {
    console.error('Error fetching user groups:', error);
    return [];
  }
}

// ✅ ADD THIS FUNCTION: Get parent org unit for search visibility
export const getParentOrgUnit = async (engine, orgUnitId) => {
  try {
    const response = await engine.query({
      orgUnit: {
        resource: 'organisationUnits',
        id: orgUnitId,
        params: {
          fields: 'id,name,parent[id,name]'
        }
      }
    });

    return response.orgUnit.parent;
  } catch (error) {
    console.error('Error fetching parent org unit:', error);
    return null;
  }
};


// Delete a staff member
export const deleteStaff = {
  resource: 'trackedEntityInstances',
  type: 'delete',
  id: ({ id }) => id
}

export const usersQuery = {
  users: {
    resource: 'users',
    params: {
      fields: 'id,displayName,userCredentials[username]',
      paging: 1000
    }
  }
}

export const staffUsersQuery = {
  staffUsers: {
    resource: 'users',
    params: {
      fields: 'id,username,firstName,surname,email,userCredentials[username,userRoles]',
      filter: `userCredentials.userRoles.id:eq:${STAFF_ROLE_ID}`,
      pageSize: 1000
    }
  },
  staffTEIs: {
    resource: 'trackedEntityInstances',
    params: {
      fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
      trackedEntityType: 'OaAbpIs1cV7',
      filter: 'n8aAqQTVI8b:!null', // Filter for TEIs with user ID attribute
      pageSize: 1000
    }
  }
}

// Add this function near your other utility functions
export const validateUserMovement = async (engine, userId, targetOrgUnitId, currentOrgUnitId) => {
  console.log('✅ Movement always allowed - Objectives handled via Position TEI');
  return {
    canMove: true,
    reason: 'Movement allowed - Objectives managed through Position TEI system'
  };
};


// Optional: wrap staffListQuery if we want to use a function called fetchStaffList
export const fetchStaffList = staffListQuery
